/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-17
 */

/**
 * Country and state/province data utilities for address forms.
 * Provides comprehensive country lists and state/province mappings.
 */

export interface Country {
  code: string;
  name: string;
  hasStates: boolean;
  stateLabel: string;
}

export interface StateProvince {
  code: string;
  name: string;
}

/**
 * Complete list of countries with metadata about state/province support.
 */
export const COUNTRIES: Country[] = [
  { code: 'AF', name: 'Afghanistan', hasStates: false, stateLabel: 'Region' },
  { code: 'AL', name: 'Albania', hasStates: false, stateLabel: 'Region' },
  { code: 'DZ', name: 'Algeria', hasStates: false, stateLabel: 'Region' },
  {
    code: 'AS',
    name: 'American Samoa',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'AD', name: 'Andorra', hasStates: false, stateLabel: 'Region' },
  { code: 'AO', name: 'Angola', hasStates: false, stateLabel: 'Region' },
  { code: 'AI', name: 'Anguilla', hasStates: false, stateLabel: 'Region' },
  { code: 'AQ', name: 'Antarctica', hasStates: false, stateLabel: 'Region' },
  {
    code: 'AG',
    name: 'Antigua and Barbuda',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'AR', name: 'Argentina', hasStates: false, stateLabel: 'Region' },
  { code: 'AM', name: 'Armenia', hasStates: false, stateLabel: 'Region' },
  { code: 'AW', name: 'Aruba', hasStates: false, stateLabel: 'Region' },
  {
    code: 'AU',
    name: 'Australia',
    hasStates: true,
    stateLabel: 'State/Territory',
  },
  { code: 'AT', name: 'Austria', hasStates: false, stateLabel: 'Region' },
  { code: 'AZ', name: 'Azerbaijan', hasStates: false, stateLabel: 'Region' },
  { code: 'BS', name: 'Bahamas', hasStates: false, stateLabel: 'Region' },
  { code: 'BH', name: 'Bahrain', hasStates: false, stateLabel: 'Region' },
  { code: 'BD', name: 'Bangladesh', hasStates: false, stateLabel: 'Region' },
  { code: 'BB', name: 'Barbados', hasStates: false, stateLabel: 'Region' },
  { code: 'BY', name: 'Belarus', hasStates: false, stateLabel: 'Region' },
  { code: 'BE', name: 'Belgium', hasStates: false, stateLabel: 'Region' },
  { code: 'BZ', name: 'Belize', hasStates: false, stateLabel: 'Region' },
  { code: 'BJ', name: 'Benin', hasStates: false, stateLabel: 'Region' },
  { code: 'BM', name: 'Bermuda', hasStates: false, stateLabel: 'Region' },
  { code: 'BT', name: 'Bhutan', hasStates: false, stateLabel: 'Region' },
  { code: 'BO', name: 'Bolivia', hasStates: false, stateLabel: 'Region' },
  {
    code: 'BA',
    name: 'Bosnia and Herzegovina',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'BW', name: 'Botswana', hasStates: false, stateLabel: 'Region' },
  { code: 'BV', name: 'Bouvet Island', hasStates: false, stateLabel: 'Region' },
  { code: 'BR', name: 'Brazil', hasStates: false, stateLabel: 'Region' },
  {
    code: 'IO',
    name: 'British Indian Ocean Territory',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'BN',
    name: 'Brunei Darussalam',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'BG', name: 'Bulgaria', hasStates: false, stateLabel: 'Region' },
  { code: 'BF', name: 'Burkina Faso', hasStates: false, stateLabel: 'Region' },
  { code: 'BI', name: 'Burundi', hasStates: false, stateLabel: 'Region' },
  { code: 'KH', name: 'Cambodia', hasStates: false, stateLabel: 'Region' },
  { code: 'CM', name: 'Cameroon', hasStates: false, stateLabel: 'Region' },
  {
    code: 'CA',
    name: 'Canada',
    hasStates: true,
    stateLabel: 'Province/Territory',
  },
  { code: 'CV', name: 'Cape Verde', hasStates: false, stateLabel: 'Region' },
  {
    code: 'KY',
    name: 'Cayman Islands',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'CF',
    name: 'Central African Republic',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'TD', name: 'Chad', hasStates: false, stateLabel: 'Region' },
  { code: 'CL', name: 'Chile', hasStates: false, stateLabel: 'Region' },
  { code: 'CN', name: 'China', hasStates: false, stateLabel: 'Region' },
  {
    code: 'CX',
    name: 'Christmas Island',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'CC',
    name: 'Cocos (Keeling) Islands',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'CO', name: 'Colombia', hasStates: false, stateLabel: 'Region' },
  { code: 'KM', name: 'Comoros', hasStates: false, stateLabel: 'Region' },
  { code: 'CG', name: 'Congo', hasStates: false, stateLabel: 'Region' },
  {
    code: 'CD',
    name: 'Congo, Democratic Republic',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'CK', name: 'Cook Islands', hasStates: false, stateLabel: 'Region' },
  { code: 'CR', name: 'Costa Rica', hasStates: false, stateLabel: 'Region' },
  { code: 'CI', name: "Cote D'Ivoire", hasStates: false, stateLabel: 'Region' },
  { code: 'HR', name: 'Croatia', hasStates: false, stateLabel: 'Region' },
  { code: 'CU', name: 'Cuba', hasStates: false, stateLabel: 'Region' },
  { code: 'CY', name: 'Cyprus', hasStates: false, stateLabel: 'Region' },
  {
    code: 'CZ',
    name: 'Czech Republic',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'DK', name: 'Denmark', hasStates: false, stateLabel: 'Region' },
  { code: 'DJ', name: 'Djibouti', hasStates: false, stateLabel: 'Region' },
  { code: 'DM', name: 'Dominica', hasStates: false, stateLabel: 'Region' },
  {
    code: 'DO',
    name: 'Dominican Republic',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'EC', name: 'Ecuador', hasStates: false, stateLabel: 'Region' },
  { code: 'EG', name: 'Egypt', hasStates: false, stateLabel: 'Region' },
  { code: 'SV', name: 'El Salvador', hasStates: false, stateLabel: 'Region' },
  {
    code: 'GQ',
    name: 'Equatorial Guinea',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'ER', name: 'Eritrea', hasStates: false, stateLabel: 'Region' },
  { code: 'EE', name: 'Estonia', hasStates: false, stateLabel: 'Region' },
  { code: 'ET', name: 'Ethiopia', hasStates: false, stateLabel: 'Region' },
  {
    code: 'FK',
    name: 'Falkland Islands (Malvinas)',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'FO', name: 'Faroe Islands', hasStates: false, stateLabel: 'Region' },
  { code: 'FJ', name: 'Fiji', hasStates: false, stateLabel: 'Region' },
  { code: 'FI', name: 'Finland', hasStates: false, stateLabel: 'Region' },
  { code: 'FR', name: 'France', hasStates: false, stateLabel: 'Region' },
  { code: 'GF', name: 'French Guiana', hasStates: false, stateLabel: 'Region' },
  {
    code: 'PF',
    name: 'French Polynesia',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'TF',
    name: 'French Southern Territories',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'GA', name: 'Gabon', hasStates: false, stateLabel: 'Region' },
  { code: 'GM', name: 'Gambia', hasStates: false, stateLabel: 'Region' },
  { code: 'GE', name: 'Georgia', hasStates: false, stateLabel: 'Region' },
  { code: 'DE', name: 'Germany', hasStates: false, stateLabel: 'Region' },
  { code: 'GH', name: 'Ghana', hasStates: false, stateLabel: 'Region' },
  { code: 'GI', name: 'Gibraltar', hasStates: false, stateLabel: 'Region' },
  { code: 'GR', name: 'Greece', hasStates: false, stateLabel: 'Region' },
  { code: 'GL', name: 'Greenland', hasStates: false, stateLabel: 'Region' },
  { code: 'GD', name: 'Grenada', hasStates: false, stateLabel: 'Region' },
  { code: 'GP', name: 'Guadeloupe', hasStates: false, stateLabel: 'Region' },
  { code: 'GU', name: 'Guam', hasStates: false, stateLabel: 'Region' },
  { code: 'GT', name: 'Guatemala', hasStates: false, stateLabel: 'Region' },
  { code: 'GG', name: 'Guernsey', hasStates: false, stateLabel: 'Region' },
  { code: 'GN', name: 'Guinea', hasStates: false, stateLabel: 'Region' },
  { code: 'GW', name: 'Guinea-Bissau', hasStates: false, stateLabel: 'Region' },
  { code: 'GY', name: 'Guyana', hasStates: false, stateLabel: 'Region' },
  { code: 'HT', name: 'Haiti', hasStates: false, stateLabel: 'Region' },
  {
    code: 'HM',
    name: 'Heard Island & Mcdonald Islands',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'VA',
    name: 'Holy See (Vatican City State)',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'HN', name: 'Honduras', hasStates: false, stateLabel: 'Region' },
  { code: 'HK', name: 'Hong Kong', hasStates: false, stateLabel: 'Region' },
  { code: 'HU', name: 'Hungary', hasStates: false, stateLabel: 'Region' },
  { code: 'IS', name: 'Iceland', hasStates: false, stateLabel: 'Region' },
  { code: 'IN', name: 'India', hasStates: false, stateLabel: 'Region' },
  { code: 'ID', name: 'Indonesia', hasStates: false, stateLabel: 'Region' },
  {
    code: 'IR',
    name: 'Iran, Islamic Republic Of',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'IQ', name: 'Iraq', hasStates: false, stateLabel: 'Region' },
  { code: 'IE', name: 'Ireland', hasStates: false, stateLabel: 'Region' },
  { code: 'IM', name: 'Isle Of Man', hasStates: false, stateLabel: 'Region' },
  { code: 'IL', name: 'Israel', hasStates: false, stateLabel: 'Region' },
  { code: 'IT', name: 'Italy', hasStates: false, stateLabel: 'Region' },
  { code: 'JM', name: 'Jamaica', hasStates: false, stateLabel: 'Region' },
  { code: 'JP', name: 'Japan', hasStates: false, stateLabel: 'Region' },
  { code: 'JE', name: 'Jersey', hasStates: false, stateLabel: 'Region' },
  { code: 'JO', name: 'Jordan', hasStates: false, stateLabel: 'Region' },
  { code: 'KZ', name: 'Kazakhstan', hasStates: false, stateLabel: 'Region' },
  { code: 'KE', name: 'Kenya', hasStates: false, stateLabel: 'Region' },
  { code: 'KI', name: 'Kiribati', hasStates: false, stateLabel: 'Region' },
  { code: 'KR', name: 'Korea', hasStates: false, stateLabel: 'Region' },
  { code: 'KW', name: 'Kuwait', hasStates: false, stateLabel: 'Region' },
  { code: 'KG', name: 'Kyrgyzstan', hasStates: false, stateLabel: 'Region' },
  {
    code: 'LA',
    name: "Lao People's Democratic Republic",
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'LV', name: 'Latvia', hasStates: false, stateLabel: 'Region' },
  { code: 'LB', name: 'Lebanon', hasStates: false, stateLabel: 'Region' },
  { code: 'LS', name: 'Lesotho', hasStates: false, stateLabel: 'Region' },
  { code: 'LR', name: 'Liberia', hasStates: false, stateLabel: 'Region' },
  {
    code: 'LY',
    name: 'Libyan Arab Jamahiriya',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'LI', name: 'Liechtenstein', hasStates: false, stateLabel: 'Region' },
  { code: 'LT', name: 'Lithuania', hasStates: false, stateLabel: 'Region' },
  { code: 'LU', name: 'Luxembourg', hasStates: false, stateLabel: 'Region' },
  { code: 'MO', name: 'Macao', hasStates: false, stateLabel: 'Region' },
  { code: 'MK', name: 'Macedonia', hasStates: false, stateLabel: 'Region' },
  { code: 'MG', name: 'Madagascar', hasStates: false, stateLabel: 'Region' },
  { code: 'MW', name: 'Malawi', hasStates: false, stateLabel: 'Region' },
  { code: 'MY', name: 'Malaysia', hasStates: false, stateLabel: 'Region' },
  { code: 'MV', name: 'Maldives', hasStates: false, stateLabel: 'Region' },
  { code: 'ML', name: 'Mali', hasStates: false, stateLabel: 'Region' },
  { code: 'MT', name: 'Malta', hasStates: false, stateLabel: 'Region' },
  {
    code: 'MH',
    name: 'Marshall Islands',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'MQ', name: 'Martinique', hasStates: false, stateLabel: 'Region' },
  { code: 'MR', name: 'Mauritania', hasStates: false, stateLabel: 'Region' },
  { code: 'MU', name: 'Mauritius', hasStates: false, stateLabel: 'Region' },
  { code: 'YT', name: 'Mayotte', hasStates: false, stateLabel: 'Region' },
  { code: 'MX', name: 'Mexico', hasStates: false, stateLabel: 'Region' },
  {
    code: 'FM',
    name: 'Micronesia, Federated States Of',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'MD', name: 'Moldova', hasStates: false, stateLabel: 'Region' },
  { code: 'MC', name: 'Monaco', hasStates: false, stateLabel: 'Region' },
  { code: 'MN', name: 'Mongolia', hasStates: false, stateLabel: 'Region' },
  { code: 'ME', name: 'Montenegro', hasStates: false, stateLabel: 'Region' },
  { code: 'MS', name: 'Montserrat', hasStates: false, stateLabel: 'Region' },
  { code: 'MA', name: 'Morocco', hasStates: false, stateLabel: 'Region' },
  { code: 'MZ', name: 'Mozambique', hasStates: false, stateLabel: 'Region' },
  { code: 'MM', name: 'Myanmar', hasStates: false, stateLabel: 'Region' },
  { code: 'NA', name: 'Namibia', hasStates: false, stateLabel: 'Region' },
  { code: 'NR', name: 'Nauru', hasStates: false, stateLabel: 'Region' },
  { code: 'NP', name: 'Nepal', hasStates: false, stateLabel: 'Region' },
  { code: 'NL', name: 'Netherlands', hasStates: false, stateLabel: 'Region' },
  {
    code: 'AN',
    name: 'Netherlands Antilles',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'NC', name: 'New Caledonia', hasStates: false, stateLabel: 'Region' },
  { code: 'NZ', name: 'New Zealand', hasStates: false, stateLabel: 'Region' },
  { code: 'NI', name: 'Nicaragua', hasStates: false, stateLabel: 'Region' },
  { code: 'NE', name: 'Niger', hasStates: false, stateLabel: 'Region' },
  { code: 'NG', name: 'Nigeria', hasStates: false, stateLabel: 'Region' },
  { code: 'NU', name: 'Niue', hasStates: false, stateLabel: 'Region' },
  {
    code: 'NF',
    name: 'Norfolk Island',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'MP',
    name: 'Northern Mariana Islands',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'NO', name: 'Norway', hasStates: false, stateLabel: 'Region' },
  { code: 'OM', name: 'Oman', hasStates: false, stateLabel: 'Region' },
  { code: 'PK', name: 'Pakistan', hasStates: false, stateLabel: 'Region' },
  { code: 'PW', name: 'Palau', hasStates: false, stateLabel: 'Region' },
  {
    code: 'PS',
    name: 'Palestinian Territory, Occupied',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'PA', name: 'Panama', hasStates: false, stateLabel: 'Region' },
  {
    code: 'PG',
    name: 'Papua New Guinea',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'PY', name: 'Paraguay', hasStates: false, stateLabel: 'Region' },
  { code: 'PE', name: 'Peru', hasStates: false, stateLabel: 'Region' },
  { code: 'PH', name: 'Philippines', hasStates: false, stateLabel: 'Region' },
  { code: 'PN', name: 'Pitcairn', hasStates: false, stateLabel: 'Region' },
  { code: 'PL', name: 'Poland', hasStates: false, stateLabel: 'Region' },
  { code: 'PT', name: 'Portugal', hasStates: false, stateLabel: 'Region' },
  { code: 'PR', name: 'Puerto Rico', hasStates: false, stateLabel: 'Region' },
  { code: 'QA', name: 'Qatar', hasStates: false, stateLabel: 'Region' },
  { code: 'RE', name: 'Reunion', hasStates: false, stateLabel: 'Region' },
  { code: 'RO', name: 'Romania', hasStates: false, stateLabel: 'Region' },
  {
    code: 'RU',
    name: 'Russian Federation',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'RW', name: 'Rwanda', hasStates: false, stateLabel: 'Region' },
  {
    code: 'BL',
    name: 'Saint Barthelemy',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'SH', name: 'Saint Helena', hasStates: false, stateLabel: 'Region' },
  {
    code: 'KN',
    name: 'Saint Kitts And Nevis',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'LC', name: 'Saint Lucia', hasStates: false, stateLabel: 'Region' },
  { code: 'MF', name: 'Saint Martin', hasStates: false, stateLabel: 'Region' },
  {
    code: 'PM',
    name: 'Saint Pierre And Miquelon',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'VC',
    name: 'Saint Vincent And Grenadines',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'WS', name: 'Samoa', hasStates: false, stateLabel: 'Region' },
  { code: 'SM', name: 'San Marino', hasStates: false, stateLabel: 'Region' },
  {
    code: 'ST',
    name: 'Sao Tome And Principe',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'SA', name: 'Saudi Arabia', hasStates: false, stateLabel: 'Region' },
  { code: 'SN', name: 'Senegal', hasStates: false, stateLabel: 'Region' },
  { code: 'RS', name: 'Serbia', hasStates: false, stateLabel: 'Region' },
  { code: 'SC', name: 'Seychelles', hasStates: false, stateLabel: 'Region' },
  { code: 'SL', name: 'Sierra Leone', hasStates: false, stateLabel: 'Region' },
  { code: 'SG', name: 'Singapore', hasStates: false, stateLabel: 'Region' },
  { code: 'SK', name: 'Slovakia', hasStates: false, stateLabel: 'Region' },
  { code: 'SI', name: 'Slovenia', hasStates: false, stateLabel: 'Region' },
  {
    code: 'SB',
    name: 'Solomon Islands',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'SO', name: 'Somalia', hasStates: false, stateLabel: 'Region' },
  { code: 'ZA', name: 'South Africa', hasStates: false, stateLabel: 'Region' },
  {
    code: 'GS',
    name: 'South Georgia And Sandwich Isl.',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'ES', name: 'Spain', hasStates: false, stateLabel: 'Region' },
  { code: 'LK', name: 'Sri Lanka', hasStates: false, stateLabel: 'Region' },
  { code: 'SD', name: 'Sudan', hasStates: false, stateLabel: 'Region' },
  { code: 'SR', name: 'Suriname', hasStates: false, stateLabel: 'Region' },
  {
    code: 'SJ',
    name: 'Svalbard And Jan Mayen',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'SZ', name: 'Swaziland', hasStates: false, stateLabel: 'Region' },
  { code: 'SE', name: 'Sweden', hasStates: false, stateLabel: 'Region' },
  { code: 'CH', name: 'Switzerland', hasStates: false, stateLabel: 'Region' },
  {
    code: 'SY',
    name: 'Syrian Arab Republic',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'TW', name: 'Taiwan', hasStates: false, stateLabel: 'Region' },
  { code: 'TJ', name: 'Tajikistan', hasStates: false, stateLabel: 'Region' },
  { code: 'TZ', name: 'Tanzania', hasStates: false, stateLabel: 'Region' },
  { code: 'TH', name: 'Thailand', hasStates: false, stateLabel: 'Region' },
  { code: 'TL', name: 'Timor-Leste', hasStates: false, stateLabel: 'Region' },
  { code: 'TG', name: 'Togo', hasStates: false, stateLabel: 'Region' },
  { code: 'TK', name: 'Tokelau', hasStates: false, stateLabel: 'Region' },
  { code: 'TO', name: 'Tonga', hasStates: false, stateLabel: 'Region' },
  {
    code: 'TT',
    name: 'Trinidad And Tobago',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'TN', name: 'Tunisia', hasStates: false, stateLabel: 'Region' },
  { code: 'TR', name: 'Turkey', hasStates: false, stateLabel: 'Region' },
  { code: 'TM', name: 'Turkmenistan', hasStates: false, stateLabel: 'Region' },
  {
    code: 'TC',
    name: 'Turks And Caicos Islands',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'TV', name: 'Tuvalu', hasStates: false, stateLabel: 'Region' },
  { code: 'UG', name: 'Uganda', hasStates: false, stateLabel: 'Region' },
  { code: 'UA', name: 'Ukraine', hasStates: false, stateLabel: 'Region' },
  {
    code: 'AE',
    name: 'United Arab Emirates',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    hasStates: true,
    stateLabel: 'County/Region',
  },
  { code: 'US', name: 'United States', hasStates: true, stateLabel: 'State' },
  {
    code: 'UM',
    name: 'United States Outlying Islands',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'UY', name: 'Uruguay', hasStates: false, stateLabel: 'Region' },
  { code: 'UZ', name: 'Uzbekistan', hasStates: false, stateLabel: 'Region' },
  { code: 'VU', name: 'Vanuatu', hasStates: false, stateLabel: 'Region' },
  { code: 'VE', name: 'Venezuela', hasStates: false, stateLabel: 'Region' },
  { code: 'VN', name: 'Viet Nam', hasStates: false, stateLabel: 'Region' },
  {
    code: 'VG',
    name: 'Virgin Islands, British',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'VI',
    name: 'Virgin Islands, U.S.',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'WF',
    name: 'Wallis And Futuna',
    hasStates: false,
    stateLabel: 'Region',
  },
  {
    code: 'EH',
    name: 'Western Sahara',
    hasStates: false,
    stateLabel: 'Region',
  },
  { code: 'YE', name: 'Yemen', hasStates: false, stateLabel: 'Region' },
  { code: 'ZM', name: 'Zambia', hasStates: false, stateLabel: 'Region' },
  { code: 'ZW', name: 'Zimbabwe', hasStates: false, stateLabel: 'Region' },
];

/**
 * United States states and territories.
 */
export const US_STATES: StateProvince[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
];

/**
 * Canadian provinces and territories.
 */
export const CANADIAN_PROVINCES: StateProvince[] = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

/**
 * Australian states and territories.
 */
export const AUSTRALIAN_STATES: StateProvince[] = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'SA', name: 'South Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NT', name: 'Northern Territory' },
];

/**
 * United Kingdom counties and regions.
 */
export const UK_COUNTIES: StateProvince[] = [
  // England
  { code: 'BEDFORDSHIRE', name: 'Bedfordshire' },
  { code: 'BERKSHIRE', name: 'Berkshire' },
  { code: 'BRISTOL', name: 'Bristol' },
  { code: 'BUCKINGHAMSHIRE', name: 'Buckinghamshire' },
  { code: 'CAMBRIDGESHIRE', name: 'Cambridgeshire' },
  { code: 'CHESHIRE', name: 'Cheshire' },
  { code: 'CORNWALL', name: 'Cornwall' },
  { code: 'CUMBRIA', name: 'Cumbria' },
  { code: 'DERBYSHIRE', name: 'Derbyshire' },
  { code: 'DEVON', name: 'Devon' },
  { code: 'DORSET', name: 'Dorset' },
  { code: 'DURHAM', name: 'Durham' },
  { code: 'EAST_SUSSEX', name: 'East Sussex' },
  { code: 'ESSEX', name: 'Essex' },
  { code: 'GLOUCESTERSHIRE', name: 'Gloucestershire' },
  { code: 'GREATER_LONDON', name: 'Greater London' },
  { code: 'GREATER_MANCHESTER', name: 'Greater Manchester' },
  { code: 'HAMPSHIRE', name: 'Hampshire' },
  { code: 'HEREFORDSHIRE', name: 'Herefordshire' },
  { code: 'HERTFORDSHIRE', name: 'Hertfordshire' },
  { code: 'ISLE_OF_WIGHT', name: 'Isle of Wight' },
  { code: 'KENT', name: 'Kent' },
  { code: 'LANCASHIRE', name: 'Lancashire' },
  { code: 'LEICESTERSHIRE', name: 'Leicestershire' },
  { code: 'LINCOLNSHIRE', name: 'Lincolnshire' },
  { code: 'MERSEYSIDE', name: 'Merseyside' },
  { code: 'NORFOLK', name: 'Norfolk' },
  { code: 'NORTHAMPTONSHIRE', name: 'Northamptonshire' },
  { code: 'NORTHUMBERLAND', name: 'Northumberland' },
  { code: 'NORTH_YORKSHIRE', name: 'North Yorkshire' },
  { code: 'NOTTINGHAMSHIRE', name: 'Nottinghamshire' },
  { code: 'OXFORDSHIRE', name: 'Oxfordshire' },
  { code: 'RUTLAND', name: 'Rutland' },
  { code: 'SHROPSHIRE', name: 'Shropshire' },
  { code: 'SOMERSET', name: 'Somerset' },
  { code: 'SOUTH_YORKSHIRE', name: 'South Yorkshire' },
  { code: 'STAFFORDSHIRE', name: 'Staffordshire' },
  { code: 'SUFFOLK', name: 'Suffolk' },
  { code: 'SURREY', name: 'Surrey' },
  { code: 'TYNE_AND_WEAR', name: 'Tyne and Wear' },
  { code: 'WARWICKSHIRE', name: 'Warwickshire' },
  { code: 'WEST_MIDLANDS', name: 'West Midlands' },
  { code: 'WEST_SUSSEX', name: 'West Sussex' },
  { code: 'WEST_YORKSHIRE', name: 'West Yorkshire' },
  { code: 'WILTSHIRE', name: 'Wiltshire' },
  { code: 'WORCESTERSHIRE', name: 'Worcestershire' },
  // Scotland
  { code: 'ABERDEENSHIRE', name: 'Aberdeenshire' },
  { code: 'ANGUS', name: 'Angus' },
  { code: 'ARGYLL_AND_BUTE', name: 'Argyll and Bute' },
  { code: 'CLACKMANNANSHIRE', name: 'Clackmannanshire' },
  { code: 'DUMFRIES_AND_GALLOWAY', name: 'Dumfries and Galloway' },
  { code: 'DUNDEE', name: 'Dundee' },
  { code: 'EAST_AYRSHIRE', name: 'East Ayrshire' },
  { code: 'EAST_DUNBARTONSHIRE', name: 'East Dunbartonshire' },
  { code: 'EAST_LOTHIAN', name: 'East Lothian' },
  { code: 'EAST_RENFREWSHIRE', name: 'East Renfrewshire' },
  { code: 'EDINBURGH', name: 'Edinburgh' },
  { code: 'FALKIRK', name: 'Falkirk' },
  { code: 'FIFE', name: 'Fife' },
  { code: 'GLASGOW', name: 'Glasgow' },
  { code: 'HIGHLAND', name: 'Highland' },
  { code: 'INVERCLYDE', name: 'Inverclyde' },
  { code: 'MIDLOTHIAN', name: 'Midlothian' },
  { code: 'MORAY', name: 'Moray' },
  { code: 'NORTH_AYRSHIRE', name: 'North Ayrshire' },
  { code: 'NORTH_LANARKSHIRE', name: 'North Lanarkshire' },
  { code: 'ORKNEY', name: 'Orkney' },
  { code: 'PERTH_AND_KINROSS', name: 'Perth and Kinross' },
  { code: 'RENFREWSHIRE', name: 'Renfrewshire' },
  { code: 'SCOTTISH_BORDERS', name: 'Scottish Borders' },
  { code: 'SHETLAND', name: 'Shetland' },
  { code: 'SOUTH_AYRSHIRE', name: 'South Ayrshire' },
  { code: 'SOUTH_LANARKSHIRE', name: 'South Lanarkshire' },
  { code: 'STIRLING', name: 'Stirling' },
  { code: 'WEST_DUNBARTONSHIRE', name: 'West Dunbartonshire' },
  { code: 'WEST_LOTHIAN', name: 'West Lothian' },
  // Wales
  { code: 'BLAENAU_GWENT', name: 'Blaenau Gwent' },
  { code: 'BRIDGEND', name: 'Bridgend' },
  { code: 'CAERPHILLY', name: 'Caerphilly' },
  { code: 'CARDIFF', name: 'Cardiff' },
  { code: 'CARMARTHENSHIRE', name: 'Carmarthenshire' },
  { code: 'CEREDIGION', name: 'Ceredigion' },
  { code: 'CONWY', name: 'Conwy' },
  { code: 'DENBIGHSHIRE', name: 'Denbighshire' },
  { code: 'FLINTSHIRE', name: 'Flintshire' },
  { code: 'GWYNEDD', name: 'Gwynedd' },
  { code: 'ISLE_OF_ANGLESEY', name: 'Isle of Anglesey' },
  { code: 'MERTHYR_TYDFIL', name: 'Merthyr Tydfil' },
  { code: 'MONMOUTHSHIRE', name: 'Monmouthshire' },
  { code: 'NEATH_PORT_TALBOT', name: 'Neath Port Talbot' },
  { code: 'NEWPORT', name: 'Newport' },
  { code: 'PEMBROKESHIRE', name: 'Pembrokeshire' },
  { code: 'POWYS', name: 'Powys' },
  { code: 'RHONDDA_CYNON_TAF', name: 'Rhondda Cynon Taf' },
  { code: 'SWANSEA', name: 'Swansea' },
  { code: 'TORFAEN', name: 'Torfaen' },
  { code: 'VALE_OF_GLAMORGAN', name: 'Vale of Glamorgan' },
  { code: 'WREXHAM', name: 'Wrexham' },
  // Northern Ireland
  { code: 'ANTRIM', name: 'Antrim' },
  { code: 'ARMAGH', name: 'Armagh' },
  { code: 'DOWN', name: 'Down' },
  { code: 'FERMANAGH', name: 'Fermanagh' },
  { code: 'LONDONDERRY', name: 'Londonderry' },
  { code: 'TYRONE', name: 'Tyrone' },
];

/**
 * State/province mappings by country code.
 */
export const STATES_BY_COUNTRY: Record<string, StateProvince[]> = {
  US: US_STATES,
  CA: CANADIAN_PROVINCES,
  AU: AUSTRALIAN_STATES,
  GB: UK_COUNTIES,
};

/**
 * Utility functions for working with country and state data.
 */

/**
 * Get a country by its code.
 */
export const getCountryByCode = (code: string): Country | undefined =>
  COUNTRIES.find(country => country.code === code);

/**
 * Get a country by its name.
 */
export const getCountryByName = (name: string): Country | undefined =>
  COUNTRIES.find(country => country.name.toLowerCase() === name.toLowerCase());

/**
 * Get states/provinces for a specific country.
 */
export const getStatesByCountryCode = (countryCode: string): StateProvince[] =>
  STATES_BY_COUNTRY[countryCode] || [];

/**
 * Check if a country has states/provinces.
 */
export const countryHasStates = (countryCode: string): boolean => {
  const country = getCountryByCode(countryCode);
  return country?.hasStates || false;
};

/**
 * Get the appropriate label for state/province field based on country.
 */
export const getStateLabel = (countryCode: string): string => {
  const country = getCountryByCode(countryCode);
  return country?.stateLabel || 'Region (Optional)';
};

/**
 * Get formatted country options for dropdowns.
 */
export const getCountryOptions = () =>
  COUNTRIES.map(country => ({
    value: country.name,
    label: country.name,
    code: country.code,
  }));

/**
 * Get formatted state options for dropdowns.
 */
export const getStateOptions = (countryCode: string) => {
  const states = getStatesByCountryCode(countryCode);
  return states.map(state => ({
    value: state.name,
    label: state.name,
    code: state.code,
  }));
};
